import "./Vessel.scss";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import CustomSelect from "../CoreComponents/CustomSelect";
import DateTimePickerComponent from "../DateTimePicker/DateTimePicker";
import { useMetadata } from "../../contexts/MetadataContext";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { containerSizeToString } from "../../utils/general";
import { Inventory } from "../../interfaces/inventory.interface";
import TableComponent from "../TableComponent/TableComponent";
import SearchBar from "../SearchBar/SearchBar";

interface VesselProps {
  setInventoryData: React.Dispatch<React.SetStateAction<any>>;
  inventoryError: boolean;
}

interface ContainerStats {
  pendingToAssign: number;
  totalContainers: number;
}

const Vessel = forwardRef<
  { resetVesselData: () => any; setVesselData: (data: any) => void },
  VesselProps
>(({ setInventoryData, inventoryError }, ref) => {
  const { containerTypes } = useMetadata();
  const { errors, touched, setFieldValue, setFieldTouched, values } =
    useFormikContext<any>();
  const [selectKey, setSelectKey] = useState(0);
  const [count, setCount] = useState<number>(0);
  const [size, setSize] = useState("");
  const [type, setType] = useState("");
  const [rows, setRows] = useState<Inventory[]>([]);
  const dateTimePickerOpenRef = useRef<{ reset: () => void }>(null);
  const dateTimePickerCloseRef = useRef<{ reset: () => void }>(null);

  // Container table states
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [containerStats, setContainerStats] = useState<ContainerStats>({
    pendingToAssign: 0,
    totalContainers: 0,
  });
  const [paginatedData, setPaginatedData] = useState<{
    data: any[];
    totalCount: number;
  }>({
    data: [],
    totalCount: 0,
  });

  // Container table columns
  const columns = [
    { header: "Container No.", accessor: "containerNo" },
    { header: "Size", accessor: "size" },
    { header: "Type", accessor: "type" },
    { header: "Status", accessor: "status" },
  ];

  // Container table actions
  const actions = [
    {
      label: "Edit",
      onClick: (row: any) => handleEditContainer(row),
    },
    {
      label: "Delete",
      onClick: (row: any) => handleDeleteContainer(row),
    },
  ];

  const resetVesselData = () => {
    dateTimePickerOpenRef.current?.reset();
    dateTimePickerCloseRef.current?.reset();
    setRows([]);
    inventoryError = false;
  };

  const setVesselData = (data: any) => {
    if (data.gateOpenDate) {
      setFieldValue("gateOpenDate", data.gateOpenDate);
    }
    if (data.gateCloseDate) {
      setFieldValue("gateCloseDate", data.gateCloseDate);
    }
    if (data.inventory) {
      setRows(data.inventory);
    }
  };

  useImperativeHandle(ref, () => ({
    resetVesselData,
    setVesselData,
  }));

  const handleAdd = () => {
    if (!count || !size || !type) return;
    setRows((prevRows) => {
      const existingRowIndex = prevRows.findIndex(
        // eslint-disable-next-line eqeqeq
        (row) => row.size == size && row.type === type
      );
      if (existingRowIndex >= 0) {
        // If a row with the same Size and Type exists, update its Count
        const updatedRows = [...prevRows];
        updatedRows[existingRowIndex].count += count;
        return updatedRows;
      } else {
        // Otherwise, add a new row
        return [...prevRows, { count, size, type }];
      }
    });
    setCount(0);
    setSize("");
    setType("");
    setSelectKey((prev) => prev + 1);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  const handleIncrementCount = (index: number) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[index].count += 1;
      return newRows;
    });
  };

  const handleDecrementCount = (index: number) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      if (newRows[index].count > 1) {
        newRows[index].count -= 1;
      }
      return newRows;
    });
  };

  useEffect(() => {
    generateDescription();
    setInventoryData(rows);
  }, [rows]);

  const generateDescription = () => {
    const jobDescription = rows
      .map(
        (row) =>
          `${row.count}-${containerSizeToString(parseInt(row.size))}-${
            row.type
          }`
      )
      .join(",");
    setFieldValue("jobDescription", jobDescription);
  };

  // Container table handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const getTotalPages = () => {
    return Math.ceil(paginatedData.totalCount / pageSize);
  };

  const handleAssignContainers = () => {
    // TODO: Implement container assignment logic
    console.log("Assign containers");
  };

  const handleOpenImportContainerDataModal = () => {
    // TODO: Implement import modal logic
    console.log("Open import modal");
  };

  const downloadExcelTemplate = () => {
    // TODO: Implement excel template download
    console.log("Download template");
  };

  const handleEditContainer = (container: any) => {
    // TODO: Implement container edit logic
    console.log("Edit container", container);
  };

  const handleDeleteContainer = (container: any) => {
    // TODO: Implement container delete logic
    console.log("Delete container", container);
  };

  // Effect to update paginated data when search or page changes
  useEffect(() => {
    // TODO: Implement actual data fetching logic
    // This is just a placeholder
    const mockData = Array(20)
      .fill(null)
      .map((_, index) => ({
        containerNo: `CONT${index + 1}`,
        size: "40",
        type: "HC",
        status: "Pending",
      }));

    const filteredData = mockData.filter((item) =>
      item.containerNo.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;

    setPaginatedData({
      data: filteredData.slice(start, end),
      totalCount: filteredData.length,
    });

    setContainerStats({
      pendingToAssign: filteredData.filter((item) => item.status === "Pending")
        .length,
      totalContainers: filteredData.length,
    });
  }, [searchQuery, currentPage, pageSize]);

  return (
    <div className="vessel-component p-[16px]">
      <div className="form-field-cover">
        <div className="form-group">
          <div className="form-field">
            <label htmlFor="vesselName" className="required_field_label">
              Vessel Name
            </label>
            <Field
              type="text"
              name="vesselName"
              placeholder="Enter Vessel Name"
              className={
                touched.vesselName && errors.vesselName ? "invalid_field" : ""
              }
            />
          </div>
          <ErrorMessage
            name="vesselName"
            component="div"
            className="validation_error"
          />
        </div>

        <div className="form-group">
          <div className="form-field">
            <label htmlFor="service">Service</label>
            <Field
              type="text"
              name="service"
              placeholder="Enter Service"
              className={
                touched.service && errors.service ? "invalid_field" : ""
              }
            />
          </div>
          <ErrorMessage
            name="service"
            component="div"
            className="validation_error"
          />
        </div>

        <div className="form-group">
          <div className="form-field">
            <label htmlFor="voa">VOA</label>
            <Field
              type="text"
              name="voa"
              placeholder="Enter VOA"
              className={touched.voa && errors.voa ? "invalid_field" : ""}
            />
          </div>
          <ErrorMessage
            name="voa"
            component="div"
            className="validation_error"
          />
        </div>

        <div className="form-group">
          <div className="form-field">
            <label htmlFor="voyageVia" className="required_field_label">
              VOY / VIA
            </label>
            <Field
              type="text"
              name="voyageVia"
              placeholder="Enter VOY / VIA"
              className={
                touched.voyageVia && errors.voyageVia ? "invalid_field" : ""
              }
            />
          </div>
          <ErrorMessage
            name="voyageVia"
            component="div"
            className="validation_error"
          />
        </div>

        <div className="form-group">
          <div className="form-field">
            <label htmlFor="gateOpenDate" className="required_field_label">
              Gate Open Date & Time
            </label>
            <DateTimePickerComponent
              skipMinDate={true}
              autoSelectDateOption="today"
              ref={dateTimePickerOpenRef}
              value={values.gateOpenDate ? values.gateOpenDate : null}
              onBlurred={() => {
                setFieldTouched("gateOpenDate", true);
              }}
              className={`h-[40px]  
                  ${
                    touched.gateOpenDate && errors.gateOpenDate
                      ? "invalid_field"
                      : ""
                  }
                  `}
              onChange={(newValue: number | null) => {
                if (newValue) {
                  setFieldValue("gateOpenDate", newValue);
                } else {
                  setFieldValue("gateOpenDate", "");
                }
              }}
            ></DateTimePickerComponent>
          </div>
          <ErrorMessage
            name="gateOpenDate"
            component="div"
            className="validation_error"
          />
        </div>

        <div className="form-group">
          <div className="form-field">
            <label htmlFor="gateCloseDate" className="required_field_label">
              Gate Close Date & Time
            </label>
            <DateTimePickerComponent
              minDate={values.gateOpenDate}
              autoSelectDateOption="tomorrow"
              ref={dateTimePickerCloseRef}
              value={values.gateCloseDate ? values.gateCloseDate : null}
              onBlurred={() => {
                setFieldTouched("gateCloseDate", true);
              }}
              className={`h-[40px]  
                  ${
                    touched.gateCloseDate && errors.gateCloseDate
                      ? "invalid_field"
                      : ""
                  }
                  `}
              onChange={(newValue: number | null) => {
                if (newValue) {
                  setFieldValue("gateCloseDate", newValue);
                } else {
                  setFieldValue("gateCloseDate", "");
                }
              }}
            ></DateTimePickerComponent>
          </div>
          <ErrorMessage
            name="gateCloseDate"
            component="div"
            className="validation_error"
          />
        </div>
      </div>

      <div className="form-group no-mb">
        <div className="form-field">
          <label htmlFor="gateOpenDate" className="required_field_label">
            Inventory
          </label>
        </div>
      </div>
      <div
        className={`bg-grayBack rounded-[10px] p-[20px] ${
          inventoryError ? "inventory_error" : ""
        }`}
      >
        <div className="flex justify-center gap-1">
          <div className="flex flex-col w-[128px]">
            <label className="block text-left text-grayText required_field_label">
              Count
            </label>
            <input
              type="number"
              name="count"
              min={0}
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value))}
              className="p-3.5 border border-gray-300 rounded h-[40px]"
              placeholder="Count"
              onFocus={(event) => {
                if (parseInt(event.target.value) === 0) {
                  event.target.select();
                }
              }}
            />
          </div>
          <div className="flex flex-col w-[92px]">
            <label className="block text-left text-grayText required_field_label">
              Size
            </label>
            <CustomSelect
              key={selectKey}
              className="h-[40px] default_border"
              onChange={(p) => setSize(p as string)}
              // options={constainerSizes}
              options={[
                { value: "20", label: "20 ft" },
                { value: "40", label: "40 ft" },
                { value: "45", label: "45 ft" },
              ]}
              label="Select Size"
              placeholder="Size"
              isMulti={false}
            />
          </div>
          <div className="flex flex-col w-[92px]">
            <label className="block text-left text-grayText required_field_label">
              Type
            </label>
            <CustomSelect
              key={selectKey}
              className="h-[40px] default_border"
              onChange={(p) => setType(p as string)}
              options={containerTypes}
              label="Select Type"
              isMulti={false}
              placeholder="Type"
            />
          </div>
        </div>
        <div className="flex justify-center my-[20px]">
          <button
            type="button"
            className="font-bold text-black rounded-[5px] border border-black h-[40px] w-[160px] disabled:opacity-50"
            onClick={handleAdd}
            disabled={!count || !size || !type}
          >
            Add Inventory
          </button>
        </div>
        {rows.length > 0 && (
          <div className="inventory-table">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="py-2 text-grayText">Count</th>
                  <th className="py-2 text-grayText">Size</th>
                  <th className="py-2 text-grayText">Type</th>
                  <th className="py-2 text-grayText"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleDecrementCount(index)}
                          className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100"
                        >
                          -
                        </button>
                        <span className="min-w-[50px] text-center">
                          {row.count}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleIncrementCount(index)}
                          className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100"
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td>{row.size} ft</td>
                    <td>{row.type} </td>
                    <td>
                      <span
                        className="link-style"
                        onClick={() => handleRemoveRow(index)}
                      >
                        Remove Item
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {inventoryError && (
        <div className="form-group no-mb">
          <div className="validation_error ">
            Add at least one inventory to proceed
          </div>
        </div>
      )}

      {/* <div className="new_changes mt-10">
        <div className="flex justify-between items-center pb-[20px]">
          <div className="mt-2">
            <SearchBar
              placeholder="Search containers..."
              onSearch={setSearchQuery}
              showClearButton={true}
              initialQuery={searchQuery}
            />
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2">
              {containerStats && containerStats.pendingToAssign > 0 && (
                <>
                  <button
                    className="assign_containers_button"
                    type="button"
                    onClick={handleAssignContainers}
                  >
                    Add Container
                  </button>

                  <button
                    className="excel_import_button"
                    type="button"
                    onClick={handleOpenImportContainerDataModal}
                  >
                    Import from Excel File
                  </button>

                  <button
                    className="excel_download_button"
                    type="button"
                    onClick={downloadExcelTemplate}
                  >
                    Download Template
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="table">
          <TableComponent
            showPagination={paginatedData.totalCount > Math.min(pageSize, 7)}
            columns={columns}
            data={paginatedData.data}
            showCheckboxes={false}
            highlightSearchMatches={true}
            searchQuery={searchQuery}
            pageSize={pageSize}
            currentPage={currentPage}
            totalPages={getTotalPages()}
            onPageChange={handlePageChange}
            onPageSizeChange={(event) =>
              handlePageSizeChange(Number(event.target.value))
            }
            showSerialNumbers={false}
            actions={actions.map((action) => ({
              ...action,
              callback: action.onClick,
            }))}
            enableRowHover={true}
          />
        </div>
      </div> */}
    </div>
  );
});

export default Vessel;
